import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ScrollText, Save, Send, Lock, Loader2 } from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlignmentStage, AwakeningStage, HarmonyStage, useStageData, STAGE_LIST } from "../components/CoachAssessmentStages";
import MatrixSummaryWidget from "../components/MatrixSummaryWidget";

const STATUS_LABELS = {
  draft: { label: "Draft", color: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  submitted: { label: "Submitted — Awaiting Coach", color: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
  finalized: { label: "Finalized by Coach", color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
};

const SCALE_COLORS = {
  1: "border-rose-500/40 text-rose-300 bg-rose-500/5 hover:bg-rose-500/15",
  2: "border-amber-500/40 text-amber-300 bg-amber-500/5 hover:bg-amber-500/15",
  3: "border-sky-500/40 text-sky-300 bg-sky-500/5 hover:bg-sky-500/15",
  4: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/15",
};

const SCALE_ACTIVE = {
  1: "border-rose-400 text-rose-200 bg-rose-500/25",
  2: "border-amber-400 text-amber-200 bg-amber-500/25",
  3: "border-sky-400 text-sky-200 bg-sky-500/25",
  4: "border-emerald-400 text-emerald-200 bg-emerald-500/25",
};

const CoachAssessmentMatrix = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [spec, setSpec] = useState(null);
  const [doc, setDoc] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePillar, setActivePillar] = useState("P1");
  const [activeStage, setActiveStage] = useState("awareness");

  // Fetch own doc first (always allowed); only fetch spec when active
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const meRes = await apiCall("GET", "/coach-assessment-matrix/me", null, token);
      if (cancelled) return;
      if (!meRes.success) {
        toast.error(meRes.error || "Failed to load matrix");
        setLoading(false);
        return;
      }
      setDoc(meRes.data);
      setResponses(meRes.data.client_responses || {});
      // Spec is fetched only when matrix is active. Coaches/admins are never the actor on /me.
      if (meRes.data.active) {
        const specRes = await apiCall("GET", "/coach-assessment-matrix/spec", null, token);
        if (!cancelled && specRes.success) setSpec(specRes.data);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const allElements = useMemo(() => {
    if (!spec) return [];
    return spec.pillars.flatMap(p => p.elements.map(e => ({ ...e, pillarCode: p.code, pillarName: p.name })));
  }, [spec]);

  const summary = useMemo(() => {
    let completed = 0;
    let total = 0;
    const elementScores = {};
    // Numerical pillars only (P1-P5)
    const numericalElements = allElements.filter(e => e.pillarCode !== "P6");
    for (const el of numericalElements) {
      const r = responses[el.code] || {};
      const hasQ1 = typeof r.q1 === "number";
      const hasQ2 = typeof r.q2 === "number";
      if (hasQ1 && hasQ2) {
        completed += 1;
        elementScores[el.code] = Math.round((r.q1 + r.q2) / 2);
        total += elementScores[el.code];
      }
    }
    const pillarAvg = {};
    if (spec) {
      for (const p of spec.pillars) {
        if (p.code === "P6") continue;
        const vals = p.elements.map(e => elementScores[e.code]).filter(v => typeof v === "number");
        pillarAvg[p.code] = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : "—";
      }
    }
    // Vasana profile (client-side compute, mirrors backend compute_vasana_profile)
    const p6 = spec?.pillars?.find(p => p.code === "P6");
    let vasana = null;
    if (p6) {
      const sets = p6.elements.map(el => {
        const entry = responses[el.code] || {};
        const subs = (el.subtypes || []).map((sub, i) => {
          const v = entry[`q${i + 1}`];
          return { code: sub.code, label: sub.label, answer: v === 1 ? "yes" : v === 0 ? "no" : null };
        });
        const yesCount = subs.filter(s => s.answer === "yes").length;
        const answered = subs.filter(s => s.answer !== null).length;
        return {
          code: el.code, label: el.name, sanskrit: el.sanskrit,
          yes_count: yesCount, answered, total: subs.length,
          strong_signal: (subs.length === 3 && yesCount === 3),
          subtypes: subs,
        };
      });
      const ranked = [...sets].sort((a, b) => (b.yes_count - a.yes_count) || (b.answered - a.answered));
      const dominant = ranked[0]?.yes_count > 0 ? ranked[0] : null;
      const secondary = dominant ? (ranked.slice(1).find(r => r.yes_count > 0) || null) : null;
      const answeredTotal = sets.reduce((a, s) => a + s.answered, 0);
      const questionTotal = sets.reduce((a, s) => a + s.total, 0);
      vasana = {
        sets, dominant, secondary,
        strong_signal_sets: sets.filter(s => s.strong_signal).map(s => s.code),
        total_answered: answeredTotal, total_questions: questionTotal,
        is_complete: answeredTotal === questionTotal,
      };
    }
    return { completed, totalElements: numericalElements.length, total, pillarAvg, vasana };
  }, [responses, allElements, spec]);

  const isFinalized = doc?.status === "finalized";
  const isSubmitted = doc?.status === "submitted";
  const isActive = doc?.active === true;
  const isReadOnly = isFinalized || !isActive;

  const handleSelect = useCallback((elementCode, q, value) => {
    if (isReadOnly) return;
    setResponses(prev => {
      const entry = { ...(prev[elementCode] || {}) };
      entry[q] = value;
      return { ...prev, [elementCode]: entry };
    });
  }, [isReadOnly]);

  // Debounced auto-save
  useEffect(() => {
    if (!doc || loading || isReadOnly) return;
    const handle = setTimeout(async () => {
      setSaving(true);
      const res = await apiCall("PUT", "/coach-assessment-matrix/me", { client_responses: responses }, token);
      setSaving(false);
      if (!res.success) toast.error(res.error || "Auto-save failed");
    }, 1200);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses]);

  const handleSaveNow = async () => {
    if (isReadOnly) return;
    setSaving(true);
    const res = await apiCall("PUT", "/coach-assessment-matrix/me", { client_responses: responses }, token);
    setSaving(false);
    if (res.success) toast.success("Saved");
    else toast.error(res.error || "Save failed");
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;
    if (summary.completed < summary.totalElements) {
      toast.error(`Complete all ${summary.totalElements} P1-P5 elements before submitting. (${summary.completed} done)`);
      return;
    }
    if (summary.vasana && summary.vasana.total_answered < summary.vasana.total_questions) {
      toast.error(`Complete all ${summary.vasana.total_questions} Romantic Vasana (P6) questions — currently ${summary.vasana.total_answered}/${summary.vasana.total_questions}.`);
      return;
    }
    setSaving(true);
    // Save first, then submit
    const saveRes = await apiCall("PUT", "/coach-assessment-matrix/me", { client_responses: responses }, token);
    if (!saveRes.success) { setSaving(false); toast.error(saveRes.error || "Save failed"); return; }
    const res = await apiCall("POST", "/coach-assessment-matrix/me/submit", {}, token);
    setSaving(false);
    if (res.success) {
      toast.success("Submitted to your coach. You can still update until they finalize.");
      setDoc(prev => ({ ...prev, status: "submitted", submitted_at: res.data.submitted_at }));
    } else {
      toast.error(res.error || "Submit failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-void flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-divine-gold animate-spin" />
      </div>
    );
  }

  // ── LOCKED PREVIEW: when matrix is not active, hide ALL questions/spec ──
  if (!doc?.active) {
    return (
      <div className="min-h-screen bg-cosmic-void text-chakra-crown" data-testid="coach-assessment-matrix-page">
        <div className="sticky top-0 z-40 bg-cosmic-void/95 backdrop-blur border-b border-divine-gold/15">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-chakra-crown/70 hover:text-divine-gold transition-colors"
              data-testid="matrix-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </button>
            <span className="px-3 py-1 rounded-full text-[11px] font-semibold border text-amber-300 bg-amber-500/10 border-amber-500/30 flex items-center gap-1" data-testid="matrix-inactive-badge">
              <Lock className="w-3 h-3" /> Locked
            </span>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="matrix-locked-screen">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-amber-500/30 bg-amber-500/5 mb-4">
              <Lock className="w-8 h-8 text-amber-300" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading text-divine-gold mb-2">Coach Assessment Matrix</h1>
            <p className="text-chakra-crown/55 text-sm italic">Cosmic Foundation · Diagnostic Assessment Tool</p>
          </div>

          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 sm:p-8 space-y-5">
            <div>
              <h2 className="text-amber-200 font-heading text-lg mb-2">This assessment is currently locked</h2>
              <p className="text-chakra-crown/75 text-sm leading-relaxed">
                The Coach Assessment Matrix is a deep 30-element Vedic diagnostic spanning the five cosmic pillars:
                <span className="text-divine-gold"> Shiva, Shakti, the Three Gunas, the Arishadvargas, and the Pañca Koshas.</span>
              </p>
            </div>

            <div className="border-t border-amber-500/15 pt-5">
              <p className="text-chakra-crown/85 text-sm font-medium mb-3">To unlock the question set, one of the following must occur:</p>
              <ul className="space-y-2.5 text-sm">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-divine-gold/15 text-divine-gold text-xs font-bold flex items-center justify-center">1</span>
                  <span className="text-chakra-crown/75"><span className="text-divine-gold">Order &amp; pay</span> for the Coach Assessment from your dashboard — activation is automatic on payment confirmation.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-divine-gold/15 text-divine-gold text-xs font-bold flex items-center justify-center">2</span>
                  <span className="text-chakra-crown/75">Your <span className="text-divine-gold">coach</span> grants you access from their client view.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-divine-gold/15 text-divine-gold text-xs font-bold flex items-center justify-center">3</span>
                  <span className="text-chakra-crown/75">A SIWON <span className="text-divine-gold">administrator</span> activates the matrix for you.</span>
                </li>
              </ul>
            </div>

            <div className="border-t border-amber-500/15 pt-5 text-[11px] text-chakra-crown/50 italic leading-relaxed">
              The question set itself is intentionally hidden until activation, so the diagnostic remains a genuine first encounter — not a study guide. This protects the integrity of your responses and the coach&apos;s interpretation.
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                onClick={() => navigate("/dashboard")}
                className="bg-divine-gold text-cosmic-void hover:bg-divine-gold/90 font-semibold"
                data-testid="matrix-locked-order-btn"
              >
                Order Coach Assessment
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
                data-testid="matrix-locked-back-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!spec || !doc) {
    return (
      <div className="min-h-screen bg-cosmic-void p-8 text-chakra-crown">
        <div className="max-w-xl mx-auto">
          <p className="text-rose-300">Matrix not available. Please order the Coach Assessment from your dashboard first.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-4">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const currentPillar = spec.pillars.find(p => p.code === activePillar) || spec.pillars[0];
  const statusMeta = STATUS_LABELS[doc.status] || STATUS_LABELS.draft;
  const progressPct = Math.round((summary.completed / summary.totalElements) * 100);

  return (
    <div className="min-h-screen bg-cosmic-void text-chakra-crown pb-32" data-testid="coach-assessment-matrix-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-cosmic-void/95 backdrop-blur border-b border-divine-gold/15">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-chakra-crown/70 hover:text-divine-gold transition-colors"
              data-testid="matrix-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </button>
            <div className="flex items-center gap-3">
              {!isActive && (
                <span className="px-3 py-1 rounded-full text-[11px] font-semibold border text-amber-300 bg-amber-500/10 border-amber-500/30 flex items-center gap-1" data-testid="matrix-inactive-badge">
                  <Lock className="w-3 h-3" /> Preview · Awaiting Activation
                </span>
              )}
              {isActive && (
                <span className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${statusMeta.color}`} data-testid="matrix-status-badge">
                  {statusMeta.label}
                </span>
              )}
              {saving && <span className="text-xs text-chakra-crown/50 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-2">
              <ScrollText className="w-5 h-5 text-divine-gold" />
              <h1 className="text-lg sm:text-xl font-heading text-divine-gold">Coach Assessment Matrix</h1>
              <span className="text-xs text-chakra-crown/50">Cosmic Foundation · Diagnostic Assessment Tool</span>
            </div>
            {/* Progress bar */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 rounded-full bg-cosmic-light/10 overflow-hidden">
                <div className="h-full bg-divine-gold transition-all" style={{ width: `${progressPct}%` }} />
              </div>
              <span className="text-xs text-chakra-crown/70 tabular-nums" data-testid="matrix-progress-text">
                {summary.completed}/{summary.totalElements} ({progressPct}%)
              </span>
            </div>
          </div>
        </div>
        {/* Pillar tabs */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3 flex gap-2 overflow-x-auto">
          {spec.pillars.map(p => {
            let completedInPillar;
            let totalInPillar;
            if (p.code === "P6") {
              // Vasana pillar: count individual Yes/No subtype answers, total = 13
              totalInPillar = p.elements.reduce((a, e) => a + (e.subtypes?.length || 0), 0);
              completedInPillar = p.elements.reduce((a, e) => {
                const r = responses[e.code] || {};
                const qcount = e.subtypes?.length || 0;
                let c = 0;
                for (let i = 1; i <= qcount; i++) if (r[`q${i}`] === 0 || r[`q${i}`] === 1) c++;
                return a + c;
              }, 0);
            } else {
              totalInPillar = p.elements.length;
              completedInPillar = p.elements.filter(e => {
                const r = responses[e.code] || {};
                return typeof r.q1 === "number" && typeof r.q2 === "number";
              }).length;
            }
            return (
              <button
                key={p.code}
                onClick={() => setActivePillar(p.code)}
                data-testid={`pillar-tab-${p.code}`}
                className={`shrink-0 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                  activePillar === p.code
                    ? "border-divine-gold text-divine-gold bg-divine-gold/10"
                    : "border-divine-gold/15 text-chakra-crown/60 hover:border-divine-gold/40 hover:text-divine-gold/80"
                }`}
              >
                <span className="font-heading">{p.code}</span> · {p.name}
                <span className="ml-2 text-[10px] opacity-70 tabular-nums">{completedInPillar}/{totalInPillar}</span>
              </button>
            );
          })}
        </div>
        {/* Stage tabs */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3 flex gap-2 overflow-x-auto border-t border-divine-gold/10 pt-3">
          {STAGE_LIST.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setActiveStage(s.key)}
                data-testid={`stage-tab-${s.key}`}
                className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                  activeStage === s.key
                    ? "border-divine-gold text-divine-gold bg-divine-gold/15"
                    : "border-divine-gold/10 text-chakra-crown/55 hover:border-divine-gold/30 hover:text-divine-gold/80"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="font-heading">{s.label}</span>
                <span className="text-[10px] opacity-70 hidden sm:inline">· {s.desc}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {!isActive && (
          <div className="mb-6 p-5 rounded-xl border border-amber-500/30 bg-amber-500/5 flex items-start gap-3" data-testid="matrix-awaiting-activation-banner">
            <Lock className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="text-amber-200 font-heading text-sm">Awaiting Coach Activation</h3>
              <p className="text-chakra-crown/70 text-xs mt-1 leading-relaxed">
                This is a preview of the full Cosmic Foundation Diagnostic — 5 numerical pillars × 30 elements × 60 questions on the 1–4 Tamas→Sattva scale, plus the P6 · Romantic Vasanas diagnostic (13 Yes/No questions across 5 Sets).
                Once your coach activates this assessment for you, your responses will be saved and reviewed by them.
              </p>
            </div>
          </div>
        )}

        <div className="mb-6 p-4 rounded-xl border border-divine-gold/15 bg-cosmic-deep/40">
          <h2 className="text-divine-gold font-heading text-lg" data-testid={`pillar-title-${currentPillar.code}`}>{currentPillar.name}</h2>
          <p className="text-chakra-crown/60 text-sm italic">{currentPillar.subtitle}</p>
        </div>

        {activeStage === "awareness" && currentPillar.question_format === "yes_no" ? (
          <VasanaAwarenessView
            pillar={currentPillar}
            responses={responses}
            profile={summary?.vasana}
            isReadOnly={isReadOnly}
            onToggle={(code, qKey, val) => handleSelect(code, qKey, val === true ? 1 : val === false ? 0 : null)}
          />
        ) : activeStage === "awareness" && (
        <div className="space-y-8">
          {currentPillar.elements.map((el, idx) => {
            const r = responses[el.code] || {};
            const elScore = (typeof r.q1 === "number" && typeof r.q2 === "number") ? Math.round((r.q1 + r.q2) / 2) : null;
            return (
              <div key={el.code} className="rounded-xl border border-divine-gold/15 bg-cosmic-deep/30 p-5" data-testid={`element-${el.code}`}>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-divine-gold font-heading text-base">
                      <span className="text-chakra-crown/40 text-sm mr-2">{idx + 1}.</span>
                      {el.name} · <span className="italic text-chakra-crown/70">{el.sanskrit}</span>
                    </h3>
                    <p className="text-chakra-crown/55 text-xs mt-1">{el.essence}</p>
                  </div>
                  {elScore !== null && (
                    <span
                      className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm border ${SCALE_ACTIVE[elScore]}`}
                      data-testid={`element-score-${el.code}`}
                      title={`Element score (rounded average of Q1 & Q2)`}
                    >
                      {elScore}
                    </span>
                  )}
                </div>

                {/* Q1 */}
                {["q1", "q2"].map((qKey, qIdx) => {
                  const qText = el[qKey];
                  const options = el[`${qKey}_options`] || [];
                  const selected = r[qKey];
                  return (
                    <div key={qKey} className="mb-5 last:mb-0">
                      <p className="text-chakra-crown/85 text-sm mb-3 leading-relaxed">
                        <span className="text-divine-gold/80 text-xs font-heading mr-2">Q{qIdx + 1}.</span>
                        {qText}
                      </p>
                      <div className="grid grid-cols-1 gap-2">
                        {options.map((opt, oi) => {
                          const val = oi + 1;
                          const isSel = selected === val;
                          const cls = isSel ? SCALE_ACTIVE[val] : SCALE_COLORS[val];
                          return (
                            <button
                              key={val}
                              type="button"
                              disabled={isReadOnly}
                              onClick={() => handleSelect(el.code, qKey, val)}
                              data-testid={`option-${el.code}-${qKey}-${val}`}
                              className={`text-left p-3 rounded-lg border text-xs transition-all flex items-start gap-3 ${cls} ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                            >
                              <span className="shrink-0 w-6 h-6 rounded-full border border-current flex items-center justify-center text-[10px] font-bold">
                                {isSel ? <CheckCircle2 className="w-3.5 h-3.5" /> : val}
                              </span>
                              <span className="leading-relaxed flex-1">{opt}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        )}

        {activeStage === "alignment" && (
          <AlignmentStageWrapper token={token} doc={doc} setDoc={setDoc} pillar={currentPillar} isReadOnly={isReadOnly} />
        )}
        {activeStage === "awakening" && (
          <AwakeningStageWrapper token={token} doc={doc} setDoc={setDoc} pillar={currentPillar} isReadOnly={isReadOnly} />
        )}
        {activeStage === "harmony" && (
          <HarmonyStageWrapper token={token} doc={doc} setDoc={setDoc} pillar={currentPillar} isReadOnly={isReadOnly} />
        )}
        {activeStage === "summary" && (
          <MatrixSummaryWidget spec={spec} doc={doc} token={token} isCoachView={false} />
        )}
      </div>

      {/* Footer action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-cosmic-void/95 backdrop-blur border-t border-divine-gold/15">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-chakra-crown/60">
            {isFinalized ? (
              <span className="flex items-center gap-1 text-emerald-300"><Lock className="w-3 h-3" /> Finalized by your coach — view only</span>
            ) : !isActive ? (
              <span className="flex items-center gap-1 text-amber-300"><Lock className="w-3 h-3" /> Preview only — awaiting coach activation</span>
            ) : (
              <>
                <span className="text-divine-gold font-heading mr-2">Total:</span>
                <span className="tabular-nums">{summary.total}</span>
                <span className="text-chakra-crown/40"> / {summary.totalElements * 4}</span>
                <span className="ml-3 text-chakra-crown/40">Range {spec.totals?.min_score || summary.totalElements}–{spec.totals?.max_score || summary.totalElements * 4}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isReadOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveNow}
                disabled={saving}
                data-testid="matrix-save-btn"
                className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
              >
                <Save className="w-3.5 h-3.5 mr-1" /> Save Draft
              </Button>
            )}
            {!isReadOnly && (
              <Button
                onClick={handleSubmit}
                disabled={saving || isSubmitted}
                data-testid="matrix-submit-btn"
                className="bg-divine-gold text-cosmic-void hover:bg-divine-gold/90 font-semibold"
              >
                <Send className="w-3.5 h-3.5 mr-1" /> {isSubmitted ? "Re-submit to Coach" : "Submit to Coach"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────── Stage wrappers (client-side) ───────────
const AlignmentStageWrapper = ({ token, doc, setDoc, pillar, isReadOnly }) => {
  const { data, handleChange, saving } = useStageData({ token, stage: "alignment", doc, isReadOnly, isClient: true });
  useEffect(() => { if (doc) setDoc(prev => ({ ...prev, alignment_progress: data })); /* sync up */ /* eslint-disable-next-line */ }, [data]);
  return (
    <>
      {saving && <p className="text-xs text-chakra-crown/40 mb-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</p>}
      <AlignmentStage pillar={pillar} progress={data} isReadOnly={isReadOnly} onChange={handleChange} />
    </>
  );
};

const AwakeningStageWrapper = ({ token, doc, setDoc, pillar, isReadOnly }) => {
  const { data, handleChange, saving } = useStageData({ token, stage: "awakening", doc, isReadOnly, isClient: true });
  useEffect(() => { if (doc) setDoc(prev => ({ ...prev, awakening_progress: data })); /* eslint-disable-next-line */ }, [data]);
  return (
    <>
      {saving && <p className="text-xs text-chakra-crown/40 mb-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</p>}
      <AwakeningStage pillar={pillar} progress={data} isReadOnly={isReadOnly} onChange={handleChange} />
    </>
  );
};

const HarmonyStageWrapper = ({ token, doc, setDoc, pillar, isReadOnly }) => {
  const { data, handleChange, saving } = useStageData({ token, stage: "harmony", doc, isReadOnly, isClient: true });
  useEffect(() => { if (doc) setDoc(prev => ({ ...prev, harmony_checklist: data })); /* eslint-disable-next-line */ }, [data]);
  return (
    <>
      {saving && <p className="text-xs text-chakra-crown/40 mb-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</p>}
      <HarmonyStage pillar={pillar} progress={data} isReadOnly={isReadOnly} onChange={handleChange} />
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// P6 · Romantic Vasanas — Awareness View
// 13 Yes/No subtype questions across 5 Sets. Set-level yes-count → Dominant /
// Secondary Tier-1 root. 3/3 set = strong signal. Rendering matches spec verbatim.
// ═══════════════════════════════════════════════════════════════════════════

const SET_COLORS = {
  P6E1: { text: "text-rose-300", border: "border-rose-400/40", bar: "bg-rose-400", bgSoft: "bg-rose-500/10" },
  P6E2: { text: "text-sky-300", border: "border-sky-400/40", bar: "bg-sky-400", bgSoft: "bg-sky-500/10" },
  P6E3: { text: "text-amber-300", border: "border-amber-400/40", bar: "bg-amber-400", bgSoft: "bg-amber-500/10" },
  P6E4: { text: "text-violet-300", border: "border-violet-400/40", bar: "bg-violet-400", bgSoft: "bg-violet-500/10" },
  P6E5: { text: "text-emerald-300", border: "border-emerald-400/40", bar: "bg-emerald-400", bgSoft: "bg-emerald-500/10" },
};

function VasanaProfileBanner({ profile }) {
  if (!profile) return null;
  const { dominant, secondary, strong_signal_sets, total_answered, total_questions, is_complete } = profile;
  return (
    <div className="mb-6 p-5 rounded-xl border border-divine-gold/25 bg-cosmic-deep/60" data-testid="vasana-profile-banner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-divine-gold font-heading text-sm tracking-wide">VASANA PROFILE · YOUR ROOT PATTERN</h3>
          <p className="text-[11px] text-chakra-crown/55 mt-0.5">
            {is_complete ? "Complete diagnostic." : `${total_answered}/${total_questions} questions answered`}
            {strong_signal_sets?.length > 0 && <span className="ml-2 text-emerald-300"> · Strong signal · {strong_signal_sets.map(c => c.replace("P6E", "Set ")).join(", ")}</span>}
          </p>
        </div>
      </div>
      {(dominant || secondary) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          {dominant && (
            <div className={`p-3 rounded-lg border ${SET_COLORS[dominant.code]?.border || ""} ${SET_COLORS[dominant.code]?.bgSoft || ""}`} data-testid="vasana-dominant">
              <p className="text-[10px] uppercase tracking-wider text-divine-gold/80">Dominant Tier-1 Root</p>
              <p className={`text-base font-heading mt-1 ${SET_COLORS[dominant.code]?.text || "text-chakra-crown"}`}>
                {dominant.label}
                {dominant.strong_signal && <span className="ml-2 text-[10px] text-emerald-300 uppercase tracking-wider">strong signal</span>}
              </p>
              <p className="text-[11px] text-chakra-crown/60 mt-0.5">{dominant.sanskrit} · {dominant.yes_count}/{dominant.total} yes</p>
            </div>
          )}
          {secondary && (
            <div className={`p-3 rounded-lg border ${SET_COLORS[secondary.code]?.border || ""} ${SET_COLORS[secondary.code]?.bgSoft || ""}`} data-testid="vasana-secondary">
              <p className="text-[10px] uppercase tracking-wider text-chakra-crown/60">Secondary Root</p>
              <p className={`text-base font-heading mt-1 ${SET_COLORS[secondary.code]?.text || "text-chakra-crown"}`}>{secondary.label}</p>
              <p className="text-[11px] text-chakra-crown/60 mt-0.5">{secondary.sanskrit} · {secondary.yes_count}/{secondary.total} yes</p>
            </div>
          )}
        </div>
      )}
      {!dominant && (
        <p className="text-xs text-chakra-crown/45 mt-3 italic">Answer questions below to reveal your dominant vasana pattern.</p>
      )}
    </div>
  );
}

function VasanaAwarenessView({ pillar, responses, profile, isReadOnly, onToggle }) {
  return (
    <div className="space-y-6" data-testid="vasana-awareness-view">
      <VasanaProfileBanner profile={profile} />
      {pillar.elements.map((el, idx) => {
        const c = SET_COLORS[el.code] || {};
        const r = responses[el.code] || {};
        const answered = (el.subtypes || []).reduce((a, _, i) => a + (r[`q${i + 1}`] === 0 || r[`q${i + 1}`] === 1 ? 1 : 0), 0);
        const yesCount = (el.subtypes || []).reduce((a, _, i) => a + (r[`q${i + 1}`] === 1 ? 1 : 0), 0);
        const isStrong = el.subtypes?.length === 3 && yesCount === 3;
        return (
          <div key={el.code} className={`rounded-xl border ${c.border || "border-divine-gold/15"} bg-cosmic-deep/30 p-5`} data-testid={`element-${el.code}`}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="min-w-0">
                <h3 className={`font-heading text-base ${c.text || "text-divine-gold"}`}>
                  <span className="text-chakra-crown/40 text-sm mr-2">Set {String.fromCharCode(65 + idx)}.</span>
                  {el.name} · <span className="italic text-chakra-crown/70 text-sm">{el.sanskrit}</span>
                </h3>
                <p className="text-chakra-crown/55 text-xs mt-1">{el.essence}</p>
              </div>
              <div className="shrink-0 text-right">
                <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-heading border ${c.border || ""} ${c.text || ""} tabular-nums`}
                  data-testid={`element-yes-count-${el.code}`}>
                  {yesCount}/{el.subtypes.length} yes
                </span>
                {isStrong && (
                  <p className="text-[10px] text-emerald-300 uppercase tracking-wider mt-1">strong signal</p>
                )}
                <p className="text-[10px] text-chakra-crown/40 mt-0.5">{answered}/{el.subtypes.length} answered</p>
              </div>
            </div>
            <div className="space-y-3">
              {el.subtypes.map((sub, i) => {
                const qKey = `q${i + 1}`;
                const val = r[qKey];
                return (
                  <div key={sub.code} className="rounded-lg border border-divine-gold/10 bg-cosmic-void/30 p-4" data-testid={`vasana-q-${el.code}-${sub.code}`}>
                    <p className="text-[10px] uppercase tracking-wider text-divine-gold/70 mb-1">{sub.label}</p>
                    <p className="text-sm text-chakra-crown/90 leading-relaxed mb-3">{sub.question}</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={isReadOnly}
                        onClick={() => onToggle(el.code, qKey, val === 1 ? null : true)}
                        data-testid={`option-${el.code}-${qKey}-yes`}
                        className={`flex-1 py-2 rounded-lg text-xs font-heading border transition-all flex items-center justify-center gap-2 ${
                          val === 1
                            ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-200"
                            : "bg-cosmic-void/40 border-divine-gold/15 text-chakra-crown/70 hover:border-divine-gold/40"
                        } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        {val === 1 && <CheckCircle2 className="w-3.5 h-3.5" />} Yes
                      </button>
                      <button
                        type="button"
                        disabled={isReadOnly}
                        onClick={() => onToggle(el.code, qKey, val === 0 ? null : false)}
                        data-testid={`option-${el.code}-${qKey}-no`}
                        className={`flex-1 py-2 rounded-lg text-xs font-heading border transition-all flex items-center justify-center gap-2 ${
                          val === 0
                            ? "bg-rose-500/20 border-rose-400/60 text-rose-200"
                            : "bg-cosmic-void/40 border-divine-gold/15 text-chakra-crown/70 hover:border-divine-gold/40"
                        } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        {val === 0 && <CheckCircle2 className="w-3.5 h-3.5" />} No
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}



export default CoachAssessmentMatrix;
